import * as React from "react";
import { IUPSReading } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import {
  IStackTokens,
  Stack,
  DefaultButton,
  PrimaryButton,
  PanelType,
  Toggle,
} from "office-ui-fabric-react";
import { getAlertDetails } from "../../../services/ThresholdService";
import CustomDrawer from "../../common/CustomDrawer";
import { Formik } from "formik";
import { updateUPSReading } from "../../../services/infrastructure/UpsService";
import * as yup from "yup";
import { CustomTextField } from "../../common/Library";
import { CategoryHeader } from "../Misc";

const currentSchema = yup.object().shape({
  R: yup.number().required("required"),
  Y: yup.number().required("required"),
  B: yup.number().required("required"),
});
const voltageSchema = yup.object().shape({
  RY: yup.number().required("required"),
  YB: yup.number().required("required"),
  BR: yup.number().required("required"),
});

const upsReadingSchema = yup.object().shape({
  inputVoltage: voltageSchema,
  inputCurrent: currentSchema,
  outputVoltage: yup.object().shape({
    RN: yup.number().required("required"),
    YN: yup.number().required("required"),
    BN: yup.number().required("required"),
  }),
  outputCurrent: currentSchema,
  // loadIn: currentSchema,
  outputKW: yup.number().required("required"),
  outputKWA: yup.number().required("required"),
  outputFrequency: yup.number().required("required"),
  battery: yup.object().shape({
    voltage: yup.number().required("required"),
    current: yup.number().required("required"),
  }),
  roomTemperature: yup.number().required("required"),
  batteryRoomTemperature: yup.number().required("required"),
  recordedBy: yup.string().required("required"),
  remarks: yup.string().optional(),
});

export interface ManageUPSDialogProps {
  data?: IUPSReading;
  closeManageDialog: any;
  handleLoading: any;
  threshold: any;
  datacenterId: string;
}

const wrapStackTokens: IStackTokens = { childrenGap: 8 };

const ManageUPSDialog: React.FC<ManageUPSDialogProps> = ({
  closeManageDialog,
  handleLoading,
  threshold,
  data,
  datacenterId,
}) => {
  // console.log("[threshold]", threshold);
  // state
  const [state, setState] = React.useState({
    reading: {
      ...data,
    },
    isLoading: false,
  } as {
    reading: IUPSReading;
    isLoading: boolean;
  });
  // custom hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  // functions
  const closeAddDialog = (data?: IUPSReading) => {
    closeManageDialog(data);
  };
  const onBlurFunc = (value: any, path: string) => {
    // console.log("onBlurFunc");
    const { showAlert, msg } = getAlertDetails(path, threshold, value);
    // console.log("[showAlert]", showAlert, msg);
    if (showAlert === true) {
      showConfirmDialog("Alert", msg as string, () => { }, "Ok", true);
    }
  };
  const isPuneDc2 = ["5f0570c337b0eb553b960600"].includes(datacenterId);
  return (
    <React.Fragment>
      <CustomDrawer
        drawerType={PanelType.medium}
        title={"Update UPS Reading"}
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={state.reading}
            // validationSchema={upsReadingSchema}
            onSubmit={async (values: IUPSReading, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              result = await updateUPSReading(obj);
              handleLoading();
              if (result?.success) {
                closeAddDialog(obj);
                showToast(`UPS Reading updated successfully`, "success");
              } else {
                showToast(`Error while updating an UPS reading`, "error");
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-toggle">
                    <Toggle
                      checked={values.isSystemOn}
                      onText="On"
                      offText="Off"
                      onChange={(e, v) => setFieldValue("isSystemOn", v)}
                      label="System"
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="Input Voltage" />
                    {/* <br />
                    <Text variant='medium'>Input Voltage</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputVoltage.RY`}
                          label={"RY"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputVoltage.YB`}
                          label={"YB"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputVoltage.BR`}
                          label={"BR"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Input Current" />
                    {/* <br />
                    <Text variant='medium'>Input Current</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputCurrent.R`}
                          label={"R"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputCurrent.Y`}
                          label={"Y"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`inputCurrent.B`}
                          label={"B"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Output Voltage" />
                    {/* <br />
                    <Text variant='medium'>Output Voltage</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputVoltage.RN`}
                          label={"RN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputVoltage.YN`}
                          label={"YN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputVoltage.BN`}
                          label={"BN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Output Current" />
                    {/* <br />
                    <Text variant='medium'>Output Current</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputCurrent.R`}
                          label={"R"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputCurrent.Y`}
                          label={"Y"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputCurrent.B`}
                          label={"B"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Load in %" />
                    {/* <br />
                    <Text variant='medium'>Load in %</Text> */}
                    {isPuneDc2 ? (
                      <Stack
                        horizontal
                        wrap
                        tokens={wrapStackTokens}
                        className="pt-24"
                      >
                        <Stack.Item grow={1}>
                          <CustomTextField
                            name={`loadInPercentage`}
                            label={"Load In %"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                          />
                        </Stack.Item>
                      </Stack>
                    ) : (
                      <Stack
                        horizontal
                        wrap
                        tokens={wrapStackTokens}
                        className="pt-24"
                      >
                        <Stack.Item grow={1}>
                          <CustomTextField
                            name={`loadIn.R`}
                            label={"R"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                          />
                        </Stack.Item>
                        <Stack.Item grow={1}>
                          <CustomTextField
                            name={`loadIn.Y`}
                            label={"Y"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                          />
                        </Stack.Item>
                        <Stack.Item grow={1}>
                          <CustomTextField
                            name={`loadIn.B`}
                            label={"B"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                          />
                        </Stack.Item>
                      </Stack>
                    )}
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Battery" />
                    {/* <br />
                    <Text variant='medium'>Battery</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`battery.voltage`}
                          label={"Voltage"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`battery.current`}
                          label={"Current"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Room Temperature °C" />
                    {/* <br />
                    <Text variant='medium'>Room Temperature °C</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`roomTemperature`}
                          label={"UPS Room"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`batteryRoomTemperature`}
                          label={"Battery Room"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Others" />
                    {/* <br />
                    <Text variant='medium'>Others</Text> */}
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputKW`}
                          label={"Output KW"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputKWA`}
                          label={"Output KWA"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={`outputFrequency`}
                          label={"Output Freq (Hz)"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="General" />
                    {/* <br />
                    <Text variant='medium'>General</Text> */}
                    <Stack tokens={wrapStackTokens} className="pt-24">
                      <Stack.Item>
                        <CustomTextField
                          name={`remarks`}
                          label={"Remarks"}
                          multiline
                          autoAdjustHeight
                        />
                      </Stack.Item>
                      <Stack.Item className="pt-24">
                        <CustomTextField
                          name={`recordedBy`}
                          label={"Shift Tech Sign"}
                          disabled
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <br />
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton type="submit" text="Update" />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageUPSDialog;
